import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, UtensilsCrossed, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { buildTableConfirmUrl } from "@/lib/quote";

const inp = "bg-[#0F172A] border-slate-700 text-white h-9";
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`; };

export const ReservationsPanel = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", date: "", time: "", people: 2, children: 0, notes: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/reservations", { params: { from_date: todayStr() } });
      setList(r.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.date) return toast.error("Nome e data obbligatori");
    try {
      await api.post("/admin/reservations", form);
      toast.success("Prenotazione aggiunta");
      setForm({ name: "", phone: "", date: "", time: "", people: 2, children: 0, notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-1 flex items-center gap-1.5"><UtensilsCrossed className="w-3.5 h-3.5" /> Prenotazioni tavoli</h3>
      <p className="text-xs text-slate-400 mb-3">Arrivano automaticamente dal sito blisscafe.it (una volta aggiunto lo snippet) oppure inseriscile a mano qui. Compaiono nel calendario, nella vista settimanale e nel calendario del personale.</p>
      <div className="flex gap-2 flex-wrap mb-3">
        <Input data-testid="res-name-input" className={`${inp} flex-1 min-w-[130px]`} placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="res-phone-input" className={`${inp} w-32`} placeholder="Telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Input data-testid="res-date-input" type="date" className={`${inp} w-38`} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <Input data-testid="res-time-input" type="time" className={`${inp} w-26`} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <Input data-testid="res-people-input" type="number" min="1" className={`${inp} w-20`} title="Persone" value={form.people} onChange={(e) => setForm({ ...form, people: Number(e.target.value) })} />
        <Input data-testid="res-notes-input" className={`${inp} flex-1 min-w-[140px]`} placeholder="Note (tavolo esterno, allergie…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <Button data-testid="res-add-button" className="bg-[#C85A32] hover:bg-[#a94a28] h-9" onClick={add}>Aggiungi</Button>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {list.map((r) => (
          <div key={r.id} data-testid={`res-row-${r.id}`} className="flex items-center gap-2 text-sm py-1.5 border-b border-slate-700/50 flex-wrap">
            <span className="font-mono-num font-bold">{r.date}</span>
            {r.time && <span className="font-mono-num text-xs text-[#F0B429]">{r.time}</span>}
            <span className="font-semibold">{r.name}</span>
            <span className="text-xs text-slate-400">x{r.people}{r.children > 0 ? ` (${r.children} bimbi)` : ""}</span>
            {r.notes && <span className="text-xs text-slate-500 truncate max-w-[200px]" title={r.notes}>{r.notes}</span>}
            <span className={`text-[10px] uppercase font-bold ${r.source === "sito" ? "text-sky-400" : "text-slate-500"}`}>{r.source}</span>
            <span className="ml-auto flex items-center gap-1">
              <a href={buildTableConfirmUrl(r)} target="_blank" rel="noreferrer" data-testid={`res-confirm-${r.id}`} title="Conferma il tavolo su WhatsApp"
                className="p-1.5 text-slate-500 hover:text-[#25D366]"><MessageCircle className="w-4 h-4" /></a>
              <button data-testid={`res-delete-${r.id}`} onClick={async () => { if (!window.confirm(`Eliminare la prenotazione di ${r.name}?`)) return; await api.delete(`/admin/reservations/${r.id}`); load(); }}
                className="text-slate-500 hover:text-rose-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
            </span>
          </div>
        ))}
        {!list.length && <p className="text-sm text-slate-500">Nessuna prenotazione futura</p>}
      </div>
    </div>
  );
};
